import React, {useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {inject, observer} from 'mobx-react'
import {calculateScrollbarSize, focus, isAndroidDevice} from 'core/utils'
import {iPhoneUnlockSound} from 'core/helpers'
import {ChatStyles, Popup} from 'components/misc'
import ChatHeader from './ChatHeader/ChatHeader'
import ChatFooter from './ChatFooter/ChatFooter'
import ChatContent from './ChatContent/ChatContent'
import ConversationPicker from './ConversationPicker/ConversationPicker'
import ChatList from './ConversationPicker/ChatList'
import MobileThumb from './ConversationPicker/MobileThumb'
import {
  useAnimatedToggleOpen,
  useConversationListener,
  useEventListener,
  useOptions,
  useResizeMode,
} from 'core/hooks'
import {MODES} from 'core/constants'
import {FREE_CHAT_WINDOW_OPENED_EVENT, freeChatWindowEnds, latestFreeChatWindowEndsAt} from 'core/freeChatWindow'
import classNames from 'classnames'
import {
  TransitionGroup,
} from 'semantic-ui-react'
import VipChatLocked from './ChatContent/VipChatLocked'

const ChatWidget = ({store, conversation, actions}) => {
  const [options] = useOptions()
  useResizeMode()
  useConversationListener(store, actions)
  const setVhVariable = () => {
    if (typeof window === 'undefined') {
      return
    }
    const vhSource = window.visualViewport?.height || window.innerHeight
    document.documentElement.style.setProperty('--vh', `${vhSource * 0.01}px`)
  }
  const widgetRef = useRef(null)
  const inputRef = useRef(null)
  const setOpen = isOpen => {
    if (isOpen) {
      actions.chat.readConversation(conversation)
      if (!isAndroidDevice) {
        focus(this.props.inputRef)
      }
      setVhVariable()
    }
    setPersistentState({isOpen}, options.hooks.onToggle)
  }
  const {animateClass, toggleOpen} = useAnimatedToggleOpen(store, setOpen)

  useEventListener('click', e => {
    const {current} = widgetRef
    if (current && !current.contains(e.target)) {
      options.hooks.onClickOutside(store.widgetState)
    }
  }, [])

  useEffect(() => {
    setPersistentState({
      showScrollbar: !!calculateScrollbarSize(),
    }, options.hooks.onMount)
    iPhoneUnlockSound()
    Object.assign(window.CCP, {
      toggleOpen,
      setOpen,
    })
    setVhVariable()
    const handleResize = () => {
      setVhVariable()
    }
    window.addEventListener('resize', handleResize)
    const visualViewport = window.visualViewport
    if (visualViewport) {
      visualViewport.addEventListener('resize', handleResize)
    }
    const handleFocusConversationFromDating = event => {
      const { conversationUuid, showFeaturedConvUnlock, username, messageId } = event.detail;
      if (showFeaturedConvUnlock) {
        const foundConversation = actions.chat.findByUserConversationUid(conversationUuid)
        if (!foundConversation) {
          return
        }
        foundConversation.featuredApUnlock.isChatLocked = true;
        actions.widget.setWidgetState({ isOpen: true })
        actions.chat.setUserConversation(conversationUuid)
      }
      actions.widget.setWidgetState({ isOpen: true })
      const targetConversation = username
        ? actions.chat.findConversationByUsername(username)
        : actions.chat.findByUserConversationUid(conversationUuid)
      if (targetConversation) {
        actions.chat.setUserConversation(targetConversation.conversationUid)
      }
      if (messageId) {
        store.scrollToMessageId = messageId
      }
    }
    window.addEventListener('focusConversationFromDating', handleFocusConversationFromDating)
    // AVN-1396: getConfig only runs when ChatFooter mounts, so a window opened while the widget is already up would
    // otherwise go unnoticed until a reload. The server still decides whether a message is free.
    const handleFreeChatWindowOpened = event => {
      const ends = freeChatWindowEnds(event.detail, Math.floor(Date.now() / 1000))
      store.freeChatWindowEndsAt = latestFreeChatWindowEndsAt(store.freeChatWindowEndsAt, ends.endsAt)
      store.freeChatWindowDiscountEndsAt = latestFreeChatWindowEndsAt(store.freeChatWindowDiscountEndsAt, ends.discountEndsAt)
    }
    window.addEventListener(FREE_CHAT_WINDOW_OPENED_EVENT, handleFreeChatWindowOpened)
    return () => {
      window.removeEventListener('resize', handleResize)
      if (visualViewport) {
        visualViewport.removeEventListener('resize', handleResize)
      }
      window.removeEventListener('focusConversationFromDating', handleFocusConversationFromDating)
      window.removeEventListener(FREE_CHAT_WINDOW_OPENED_EVENT, handleFreeChatWindowOpened)
      options.hooks.onUnmount(store.widgetState)
    }
  }, [])

  const setPersistentState = (state, callback) => {
    actions.widget.setWidgetState(state)
    callback(store.widgetState)
  }

  const {mode, styles, stickers, features, translations, siteStyles = {}, chatListStyles = null} = options
  const isEmpty = !(store.visibleConversations || []).length
  const {isOpen, showScrollbar} = store.widgetState
  const scrollbarClass = showScrollbar == null ? 'unknown' : showScrollbar ? 'with' : 'no'
  const stickersClass = stickers?.length ? 'with' : 'no'
  const browser = navigator.userAgent.match(/firefox/i) ? 'firefox' : ''
  const TopComponent = mode === MODES.SIDEBAR ? ChatList : isEmpty ? () => null : ConversationPicker
  const creditPlaceholderText = store.user.premium && features.isCreditSite ? translations.creditsPerMessage : null
  const conversationUid = store.conversationUid

  const siteStylesToMerge = (chatListStyles || siteStyles ) || {}
  const mergedStyles = {
    ...styles,
    ...siteStylesToMerge,
  }

  const shouldShowFeaturedChatLocked = features.featuredFunctionality && store.conversation?.featuredApUnlock?.isChatLocked;
  if (isEmpty && (!features.emptyState || mode !== MODES.SIDEBAR)) {
    return null
  }

  if (mode === MODES.CHAT_LIST) {
    return (
      <>
        <ChatStyles variables={mergedStyles} name={mergedStyles.name} cssVars={features.cssVars}/>
        <div ref={widgetRef}
            className={`chat-widget ${'chat-list'}-mode ${animateClass} ${browser} ${isEmpty ? 'chat-empty' : ''} chat-widget-${scrollbarClass}-scrollbar ${stickersClass}-stickers chat-widget-${isOpen ? 'open' : 'minimized'}`}>
              <ChatHeader
                isOpen={isOpen}
                toggleOpen={toggleOpen}
                options={options}
                mergedStyles={mergedStyles}
                parentSite={options.parentSite || ''}
              />
              <TransitionGroup animation="browse" duration={200}>
               {!conversationUid && <ChatList options={options}/>}
              </TransitionGroup>
              <TransitionGroup animation="browse" duration={200}>
                {isEmpty ? features.emptyState?.() : conversationUid &&
                  <>
                    <div className={classNames('chat-content', { hide: !isOpen, hidden: !conversationUid })}>
                      <ChatContent options={options} conversation={store.conversation}/>
                      <ChatFooter options={options} inputRef={inputRef} creditPlaceholderText={creditPlaceholderText} setVhVariable={setVhVariable}/>
                      {shouldShowFeaturedChatLocked && <VipChatLocked profile={store.conversation.otherUserProfile} options={options}/>}
                    </div>
                    {store.popupType && <Popup/>}
                  </>
                }
              </TransitionGroup>
        </div>
        <MobileThumb toggleOpen={toggleOpen} options={options}/>
      </>
    )
  }
  return (
    <>
      <ChatStyles variables={mergedStyles} name={mergedStyles.name} cssVars={features.cssVars}/>
      <div ref={widgetRef}
           className={`chat-widget ${mode}-mode ${animateClass} ${browser} ${isEmpty ? 'chat-empty' : ''} chat-widget-${scrollbarClass}-scrollbar ${stickersClass}-stickers chat-widget-${isOpen ? 'open' : 'minimized'}`}>
        <TopComponent isOpen={isOpen} toggleOpen={toggleOpen} scrollbar={showScrollbar} options={options}/>

        {isEmpty ? features.emptyState() : <>
          <ChatHeader isOpen={isOpen} toggleOpen={toggleOpen} options={options}/>

          <div className={'chat-content ' + (!isOpen ? 'hide' : '')}>
            <ChatContent options={options} conversation={store.conversation}/>
            <ChatFooter options={options} inputRef={inputRef} creditPlaceholderText={creditPlaceholderText} mode={mode}/>
            {shouldShowFeaturedChatLocked && <VipChatLocked profile={store.conversation.otherUserProfile} options={options}/>}
          </div>
          {store.popupType && <Popup/>}
        </>}
      </div>
      {mode !== MODES.SIDEBAR && <MobileThumb toggleOpen={toggleOpen} options={options}/>}
    </>
  )
}

ChatWidget.defaultProps = {
  conversation: {},
}

ChatWidget.propTypes = {
  conversation: PropTypes.object,
}

export default inject('store', 'actions')(observer(ChatWidget))
